import CircleIcon from '@mui/icons-material/Circle';
import { Timeline, TimelineItem } from '@mui/lab';
import { Box, Grid, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getClientById,
  getActivityLogs,
  ActivityLogDTO,
  getActivityLogsByEntity,
  ClientDTO,
  ClientOwnerDTO,
  getClientStatusListByVisibility,
  NoteDTO,
  getAllNotes,
  getTotalClientContactCount,
  getChildClientsByParentId,
  checkAccessToClient,
  getJobByJobCode,
  getAllTagsByClientId,
  LogContextType,
  markClientAsDnc,
  DncDetailsDTO,
} from 'wisestep-api';
import {
  getFormatDate,
  WsColors,
  WsTypoColors,
  WsDrawer,
  WsTypoVariants,
  WsTimeLine,
  formatDateInDayMonthYearTime,
  WsTypography,
  WsSelectObj,
  ScrollableButtonTabs,
  WsPageHeader,
} from 'wisestep-base';

import { AccessControl } from '../../components';
import { NotesParent } from '../../components/communicationBar/Notes';
import { IBreadCrumbsList, WsBreadCrumbs } from '../../components/WsBreadCrumbs';
import { WsTabs } from '../../components/WsTabs';
import { useWorkspaceContext } from '../../context/WorkspaceContext';
import { WsActivityProps } from '../../features/activityLog/ActivityHelper';
import { useClientAuthz, ClientAction } from '../../hooks/useClientAuthz';
import { useDialog } from '../../hooks/useDialog';
import getEncodedQueryParams from '../../utils/getEncodedQueryParams';
import { failed, success } from '../../utils/NotificationMessages';
import { useNotificationStore } from '../../utils/NotificationStore';
import { openUrlInNewTab } from '../../utils/openUrlInNewTab';
import ComposableDashboardFiltersContextProvider from '../analyticsPage/context/ComposableDashboardFiltersContext';
import { getFile } from '../careerPageSettings/util/saveCareerPageSettings';
import { mapDncDetailsDtoToIdncByTeam } from '../clientContacts/helper/ContactListingHelper';
import ClientOwnerPopUp from '../clientListingPage/components/ClientOwnerPopUp';
import CreateClientDrawer from '../clientListingPage/components/CreateClientDrawer';
import { buildLeadUrlFromId } from '../clientListingPage/helper/ClientListingHelper';
import { useActiveLeadStatus } from '../clientsSettings/hooks/useActiveLeadStatus';
import DataPrivacy from '../conversationWidget/components/DataPrivacy';
import CrmJobList from '../crmJobs/components/CrmJobList';
import { buildCrmJobFilter } from '../crmJobs/helper/crmJobHelper';
import { defaultQuery } from '../jobs/listing/JObSearchFilterDefaultQuery';
import LeadList from '../leads/components/LeadList';
import { buildLeadFilter } from '../leads/helper/leadHelper';
import UserProfileOverview from '../recruiterProfileDetails/components/userProfile/UserProfileOverview';

import BankDetailsTab from './components/bankDetailsTab';
import ChildClientsTab from './components/ChildClientsTab';
import ClientContactsTab from './components/clientContactsTab';
import ClientSharingTable from './components/clientSharing/ClientSharingTable';
import ClientStatusUpdateForm from './components/clientStatus/ClientStatusUpdateForm';
import CompanyInfoTab from './components/companyInfoTab';
import ContractArrangementsTab from './components/contractArrangementsTab';
import DocumentsTab from './components/documentsTab';
import LegalIdentifiersTab from './components/legalIdentifiersTab';
import ComposeNote from './components/notes/ComposeNote';
import { notesAmplitudeEvents } from './components/notes/helper/notesHelper';
import {
  clientsAmplitudeEvents,
  filteredTabNames,
  getClientOrContactListBreadCrumbs,
  limitedPrivacyOptions,
} from './helper/clientDetailsHelper';
import openNotesFormDialog from './helper/openNotesFormDialog';
import { useClientStore } from './store/useClientStore';

const optionsData: WsSelectObj[] = [];

const ClientDetails = () => {
  const [param] = useSearchParams();
  const workspace = useWorkspaceContext();
  const isChild: boolean = param.get('isChild') === 'true';
  const parentName: string = param.get('name') ?? '';
  const tabIndexParam = param.get('tabIndex') ?? '0';
  const jobCode = param.get('jobCode') ?? '';
  const clientID = param.get('clientId') ?? '';
  const [companyInfo, setCompanyInfo] = useState<ClientDTO>();
  const setClientName = useClientStore((state) => state.setClientName);
  const breadCrumbsList: IBreadCrumbsList[] = getClientOrContactListBreadCrumbs(
    param,
    companyInfo?.name,
    'CLIENTS'
  );
  const [tabIndex, setTabIndex] = useState(parseInt(tabIndexParam, 10));
  const [client, setClient] = useState<ClientDTO>();
  const [openDialog, closeDialog] = useDialog();
  const setNotification = useNotificationStore((state) => state.setNotification);
  const isAuthorizedViewClientDetails = useClientAuthz(ClientAction.VIEW_CLIENT_DETAILS);
  const isAuthorizedViewClientListing = useClientAuthz(ClientAction.VIEW_CLIENT_LISTING_ALL);
  const isAuthorizedViewLegalIndetifiers = useClientAuthz(ClientAction.VIEW_LEGAL_IDENTIFIERS);
  const isAuthorizedViewBankingInformation = useClientAuthz(ClientAction.VIEW_BANKING_INFORMATION);
  const isAuthorizedViewViewUserContactDetails = useClientAuthz(
    ClientAction.VIEW_USER_CONTACT_DETAILS
  );
  const isAuthorizedViewFeeArrangements = useClientAuthz(ClientAction.VIEW_FEE_ARRANGEMENTS);
  const isAuthorizedViewAnalytics = useClientAuthz(ClientAction.VIEW_ANALYTICS);
  const isAuthorizedViewInvoices = useClientAuthz(ClientAction.VIEW_INVOICES);
  const isAuthorizedViewPosition = useClientAuthz(ClientAction.VIEW_POSITIONS);
  const isAuthorizedViewFilles = useClientAuthz(ClientAction.VIEW_FILES);
  const isAuthorizedAddAssignClient = useClientAuthz(ClientAction.ADD_ASSIGN_CLIENT);
  const isAuthorizedEditClientDetails = useClientAuthz(ClientAction.EDIT_CLIENT_DETAILS);
  const [open, setOpen] = useState(false);
  const [clientId, setClientId] = useState<string>(clientID);
  const [getActivityData, setgetActivityData] = useState<ActivityLogDTO[]>([]);
  const [parentTabIndex, setParentTabIndex] = useState<number>(parseInt(tabIndexParam, 10));
  const [totalClientContactCount, setTotalClientContactCount] = useState<number>(0);
  const [notes, setNotes] = useState<NoteDTO[]>([]);
  const [childClients, setChildClients] = useState<ClientDTO[]>([]);
  const [hasAccessToClient, setHasAccessToClient] = useState<boolean>(false);
  const [tags, setTags] = useState<string[]>([]);
  const [openCreateClientDrawer, setOpenCreateClientDrawer] = useState<boolean>(false);
  const activeStatus = useActiveLeadStatus();
  const [clientImageUrl, setClientImageUrl] = useState<string | null>(null);
  const [isInDnc, setIsInDnc] = useState<boolean>(false);
  const [openDNC, setOpenDNC] = useState<boolean>(false);

  const moreOptionList = useMemo(() => {
    const list = ['Create Note', 'Update Status', 'Change Client Owner'];
    if (isAuthorizedAddAssignClient) list.push('Assign user');
    if (isAuthorizedViewPosition) list.push('Jobs');
    if (isAuthorizedEditClientDetails) list.push('Edit Client');
    return list;
  }, [isAuthorizedAddAssignClient, isAuthorizedViewPosition, isAuthorizedEditClientDetails]);

  const activityLists: WsActivityProps[] = [];

  const notesLists: WsActivityProps[] = [];

  const reload = () => setOpen(false);

  useEffect(() => {
    const fetchData = async () => {
      if (jobCode) {
        const jobResponse = await getJobByJobCode(jobCode);
        setClientId(jobResponse?.data?.clientId ?? clientID);
      }
      const tagsRes = await getAllTagsByClientId(clientID);
      setTags(tagsRes.data);
    };

    fetchData();
  }, [jobCode]);

  useEffect(() => {
    let avtivityLog: any = [];
    setParentTabIndex(parseInt(tabIndexParam, 10));
    const fetchDataLog = async () => {
      if (clientId !== undefined) {
        try {
          const { data: actvityResponseContext } = await getActivityLogs(clientId ?? '');
          const { data: actvityResponseEnitiy } = await getActivityLogsByEntity(clientId ?? '');
          avtivityLog = actvityResponseContext.concat(actvityResponseEnitiy);
          const uniqueAvtivityLog = avtivityLog.filter((obj, index) => {
            return index === avtivityLog.findIndex((o) => obj.id === o.id);
          });
          setgetActivityData(uniqueAvtivityLog);
        } catch {
          setNotification(failed(`Failed to fetch Activity Logs`));
        }
      }
    };
    fetchDataLog();
  }, [tabIndexParam, clientId, setNotification]);

  useEffect(() => {
    const fetchData = async () => {
      if (!clientId) return;
      try {
        const response = await getClientById(clientId as string);
        setCompanyInfo(response.data.data);
        setClientName(response?.data?.data?.name ?? '');
        setIsInDnc(response?.data?.data?.dncDetails?.isInDnc ?? false);
        setClient(response.data.data);
        try {
          if (response?.data?.data?.imageUrl) {
            const fileUrl = await getFile(response.data.data.imageUrl);
            fileUrl && setClientImageUrl(fileUrl);
          }
        } catch (e) {
          console.warn('Failed to fetch client image:', e);
        }
        try {
          const clientContactCountRes = await getTotalClientContactCount(clientId as string);
          setTotalClientContactCount(clientContactCountRes.data.data ?? '');
        } catch (e) {
          console.warn('Failed to fetch client contact count:', e);
        }
        try {
          const childClientResponse = await getChildClientsByParentId(clientId as string, 0, 16);
          setChildClients(childClientResponse.data.data);
        } catch (e) {
          console.warn('Failed to fetch child clients:', e);
        }
        try {
          const notesResponse = await getAllNotes(response.data.data.id);
          setNotes(notesResponse.data);
        } catch (e) {
          console.warn('Failed to fetch notes:', e);
        }
        try {
          const res = await getClientStatusListByVisibility();
          optionsData.length = 0;
          res.data.forEach((data) =>
            optionsData.push({ label: data.clientStatusName, value: data.id })
          );
        } catch (e) {
          console.warn('Failed to fetch client status list:', e);
        }
        try {
          const responselog = await getActivityLogs(clientId);
          setgetActivityData(responselog.data);
        } catch (e) {
          console.warn('Failed to fetch activity logs:', e);
        }
      } catch (e) {
        setNotification(failed(`Failed to fetch company Information`));
      }
    };
    fetchData();
  }, [setNotification, clientId, companyInfo?.id]);

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const access = companyInfo?.id && (await checkAccessToClient(companyInfo?.id)).data?.status;
        setHasAccessToClient(access);
      } catch (error) {
        console.log('error fetching data', error);
      }
    };
    checkAccess();
  }, [companyInfo?.id]);

  // need to keep this function in comment for now, in next requirement we might use this.
  // const navigateToClientUrl = () => {
  //   const url = companyInfo?.website ?? '';
  //   const fullUrl = url.startsWith('http://') || url.startsWith('https://') ? url : `http://${url}`;
  //   window.open(fullUrl, '_blank');
  // };

  const openDrawer = () => {
    clientsAmplitudeEvents('client_details_assign', 'Client Details Assign', workspace);
    setOpen(true);
  };

  const navigateToEditClient = () => {
    clientsAmplitudeEvents('client_details_edit', 'Client Details Edit', workspace);
    setOpenCreateClientDrawer(true);
  };

  const handleJobsNavigation = () => {
    clientsAmplitudeEvents('client_details_jobs', 'Client Details Jobs', workspace);
    openUrlInNewTab(
      `/jobs?d=${getEncodedQueryParams({
        ...defaultQuery,
        clientList: [{ name: companyInfo?.name }],
      })}`
    );
  };

  const handleClientInfoTab = (index) => {
    const eventMap = {
      0: 'client_details_basic_info',
      1: 'client_details_fee_arrangements',
      2: 'client_details_bank_details',
      3: 'client_details_legal_identifiers',
    };
    clientsAmplitudeEvents(eventMap[index], 'Client Info', workspace);
    setTabIndex(index);
  };

  const onSave = (client: ClientDTO) => {
    closeDialog();
    setCompanyInfo(client);
    setClientName(client?.name);
  };

  const handleTabIndexChange = (index: number) => {
    const tabs = filteredTabNames(isChild, 0, 0);
    tabs?.[index].tabName === 'Activity' &&
      notesAmplitudeEvents(
        isChild ? 'note_client' : 'note_child_client',
        isChild ? 'Note Client' : 'Note Child Client',
        workspace
      );
    setParentTabIndex(index);
    clientsAmplitudeEvents(
      `client_details_${tabs?.[index].tabName?.toLowerCase()}`,
      `Client Details ${tabs?.[index].tabName}`,
      workspace
    );
  };

  const openClientStatusFormDialog = (defaultValues?) => {
    clientsAmplitudeEvents('client_details_status_update', 'Client Details Status', workspace);
    openDialog({
      children: (
        <ClientStatusUpdateForm
          onSave={onSave}
          onCancel={closeDialog}
          options={optionsData}
          clientID={client?.id}
          clientName={client?.name}
          {...(defaultValues && { defaultValues })}
        />
      ),
    });
  };

  notes?.map((data: NoteDTO) => {
    notesLists.push({
      activity: (
        <TimelineItem>
          <Grid container>
            <Grid item xs={12}>
              <WsTypography color={WsTypoColors.TERTIARY}>
                {` At ${formatDateInDayMonthYearTime(data?.createdOn?.toString())} by ${
                  data.createdByName
                }`}
              </WsTypography>
            </Grid>
            <Grid item xs={12}>
              <WsTypography variant={WsTypoVariants.SUB_TITLE}>
                {`${data.heading} -> ${data.description} 
                `}
              </WsTypography>
            </Grid>
          </Grid>
        </TimelineItem>
      ),
      leftChildren: <CircleIcon sx={{ color: WsColors.TheBlues600 }} fontSize={'small'} />,
    });
  });

  getActivityData?.map((data: ActivityLogDTO) =>
    activityLists.push({
      activity: (
        <TimelineItem>
          <Grid container>
            <Grid item xs={12}>
              <WsTypography color={WsTypoColors.TERTIARY}>
                {` At ${formatDateInDayMonthYearTime(data?.createdOn?.toString())} by ${
                  data.createdByName
                }`}
              </WsTypography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant={WsTypoVariants.SUB_TITLE}>
                {`${data.field} Category -> ${data.description} client ${data.parentEntitiyName}  
                `}
              </Typography>
            </Grid>
          </Grid>
        </TimelineItem>
      ),
      leftChildren: <CircleIcon sx={{ color: WsColors.TheBlues600 }} fontSize={'small'} />,
    })
  );
  if (!client) return null;

  const handleAddClientNote = () => {
    clientsAmplitudeEvents('client_details_notes', 'Client Details Note Add', workspace);
    openNotesFormDialog({
      clientId: companyInfo?.id ?? '',
      isClient: true,
      clientName: companyInfo?.name,
      openDialog,
      closeDialog,
    });
  };

  const openClientOwnerFormDialog = (clientId: string, clientOwner: ClientOwnerDTO | null) => {
    openDialog({
      children: (
        <ClientOwnerPopUp
          id={clientId}
          entityType="client"
          clientOwner={clientOwner}
          onClientSave={(updatedClient: ClientDTO) => {
            setCompanyInfo(updatedClient);
            closeDialog();
          }}
          onCancel={closeDialog}
          open={true}
        />
      ),
    });
  };

  const onMoreOptionClick = (option: string) => {
    const handler = moreOptionHandlers.get(option);
    handler?.();
  };

  const moreOptionHandlers = new Map<string, () => void>([
    ['Create Note', handleAddClientNote],
    ['Update Status', openClientStatusFormDialog],
    [
      'Change Company Owner',
      () => openClientOwnerFormDialog(companyInfo?.id ?? '', companyInfo?.owner ?? null),
    ],
    ['Assign user', openDrawer],
    ['Jobs', handleJobsNavigation],
    ['Edit Company', navigateToEditClient],
  ]);

  const handleDncSubmit = async (clientId: string, data: DncDetailsDTO) => {
    try {
      await markClientAsDnc(clientId, data);
      setNotification(success('Client marked as DNC successfully !!'));
      setIsInDnc(data.isInDnc);
      setOpenDNC(false);
    } catch (e) {
      setNotification(failed('Something went wrong, Please try again !!'));
    }
  };

  return hasAccessToClient && isAuthorizedViewClientListing ? (
    <>
      {companyInfo && (
        <>
          <WsBreadCrumbs breadCrumbsList={breadCrumbsList ?? []} />
          <WsPageHeader
            title={companyInfo?.name}
            badgeLable={
              companyInfo?.clientStatus
                ? companyInfo.clientStatus.clientStatusName
                : companyInfo.isActive
                ? 'Active'
                : 'Inactive'
            }
            hyperLink={{
              buttonName: 'View Lead(s)',
              path: buildLeadUrlFromId(companyInfo?.id ?? '', activeStatus, 'client') ?? '',
            }}
            subtitle={`Created on ${getFormatDate(companyInfo?.createdOn?.toString() ?? '')}`}
            socialUrl={companyInfo?.linkedinUrl}
            profileImg={{ profileImage: clientImageUrl ?? '', required: true }}
            moreOptions={moreOptionList}
            onMoreOptionClick={(option) => onMoreOptionClick(option)}
            text={isInDnc ? 'Do Not Contact' : ''}
          />

          <Grid item borderRadius={2} sx={{ background: WsColors.White }} mt={2} mb={1}>
            <ScrollableButtonTabs
              scrollList={filteredTabNames(
                isChild,
                totalClientContactCount,
                childClients?.[0]?.totalChildCount ?? 0
              )}
              getTabIndex={handleTabIndexChange}
              defaultValue={parentTabIndex}
            />
          </Grid>
          {parentTabIndex == 0 &&
            (isAuthorizedViewAnalytics && isAuthorizedViewClientListing ? (
              <ComposableDashboardFiltersContextProvider>
                <UserProfileOverview tabIndex={tabIndex} clientName={companyInfo.name} />
              </ComposableDashboardFiltersContextProvider>
            ) : (
              <AccessControl />
            ))}
          {parentTabIndex == 1 && (
            <WsTabs
              onChange={(index) =>
                notesAmplitudeEvents(
                  index == 0 ? 'client_details_notes' : 'client_details_activities',
                  'Client Details',
                  workspace
                )
              }
            >
              <WsTabs.Item label="Notes">
                <ComposeNote
                  parent={NotesParent.CLIENT}
                  parentId={companyInfo.id}
                  isClient={true}
                />
              </WsTabs.Item>
              <WsTabs.Item label="Activities">
                {getActivityData && (
                  <Grid item xs={12} borderRadius={2} pl={2} sx={{ background: WsColors.White }}>
                    <Timeline>
                      <Box ml="-100%">
                        <WsTimeLine activityList={activityLists} timeLineDotVariant={'outlined'} />
                      </Box>
                    </Timeline>
                  </Grid>
                )}
              </WsTabs.Item>
            </WsTabs>
          )}
          {parentTabIndex == 2 && isAuthorizedViewInvoices && isChild && (
            <ChildClientsTab
              clientId={companyInfo.id}
              parentName={companyInfo.name}
              childClients={childClients}
              totalChildCount={childClients?.[0]?.totalChildCount ?? 0}
            />
          )}
          {(isChild ? parentTabIndex == 3 : parentTabIndex == 2) && (
            <WsTabs onChange={handleClientInfoTab}>
              {isAuthorizedViewClientDetails && (
                <WsTabs.Item label="Basic Information">
                  <CompanyInfoTab
                    clientId={companyInfo.id}
                    isChild={isChild}
                    parentName={parentName}
                  />
                </WsTabs.Item>
              )}
              {isAuthorizedViewFeeArrangements && (
                <WsTabs.Item label="Fee Arrangements">
                  <ContractArrangementsTab
                    clientId={companyInfo.id}
                    clientName={companyInfo?.name ?? ''}
                  />
                </WsTabs.Item>
              )}
              {isAuthorizedViewBankingInformation && (
                <WsTabs.Item label="Bank Details">
                  <BankDetailsTab clientId={companyInfo.id} country={companyInfo?.country ?? ''} />
                </WsTabs.Item>
              )}
              {isAuthorizedViewLegalIndetifiers && (
                <WsTabs.Item label="Legal Identifiers">
                  <LegalIdentifiersTab
                    clientId={companyInfo.id}
                    clientLegalName={companyInfo?.legalName ?? ''}
                  />
                </WsTabs.Item>
              )}
            </WsTabs>
          )}
          {(isChild ? parentTabIndex == 4 : parentTabIndex == 3) && isAuthorizedViewFilles && (
            <DocumentsTab clientId={companyInfo.id} clientName={companyInfo?.name ?? ''} />
          )}
          {(isChild ? parentTabIndex == 5 : parentTabIndex == 4) &&
            isAuthorizedViewViewUserContactDetails && (
              <ClientContactsTab
                clientId={companyInfo.id}
                clientName={companyInfo?.name ?? ''}
                parentName={parentName}
                totalClientContactCount={totalClientContactCount}
                tags={tags}
              />
            )}
          {(isChild ? parentTabIndex == 6 : parentTabIndex == 5) && (
            <LeadList
              initialFilter={buildLeadFilter('client', companyInfo.id, companyInfo.name)}
              fromClientTab
            />
          )}
          {(isChild ? parentTabIndex == 7 : parentTabIndex == 6) && (
            <CrmJobList
              initialFilter={buildCrmJobFilter('client', companyInfo.id)}
              fromClientTab={true}
            />
          )}
        </>
      )}
      {open && (
        <WsDrawer
          open={open}
          onClose={reload}
          title={'Company Access'}
          headerSize="medium"
          size="small"
        >
          <ClientSharingTable clientId={clientId ?? ''} moduleName="CLIENT" />
        </WsDrawer>
      )}
      {openCreateClientDrawer && (
        <CreateClientDrawer
          open={openCreateClientDrawer}
          onClose={() => setOpenCreateClientDrawer(false)}
          client={companyInfo || null}
          postSubmit={(updatedClient) => {
            setCompanyInfo(updatedClient);
            setClientImageUrl(updatedClient?.imageUrl ?? null);
            setOpenCreateClientDrawer(false);
          }}
        />
      )}
      {openDNC && (
        <WsDrawer open={openDNC} onClose={() => setOpenDNC(false)}>
          <DataPrivacy
            email={''}
            candidateName={companyInfo?.name ?? ''}
            isInDnc={companyInfo?.dncDetails?.isInDnc ?? false}
            dncDetails={
              companyInfo?.dncDetails ? mapDncDetailsDtoToIdncByTeam(companyInfo.dncDetails) : null
            }
            privacyOptions={limitedPrivacyOptions}
            restrictLabel="DNC Up to *"
            entityId={(companyInfo?.id as string) ?? ''}
            entityName={companyInfo?.name}
            logContextType={LogContextType.Client}
            onSubmitApi={(data) => handleDncSubmit(companyInfo?.id as string, data)}
            enableActivityLog={true}
          />
        </WsDrawer>
      )}
    </>
  ) : (
    <AccessControl />
  );
};

export default ClientDetails;
